import { deferIdle } from "./deferIdle";
import { API_BASE_URL } from "../config/apiBase";

const AUDIT_URL = `${API_BASE_URL}/api/audit/log`;

function getToken() {
  return localStorage.getItem("token");
}

function compact(value, fallback = "") {
  return String(value || fallback).slice(0, 500);
}

function buildPayload(action, status, details = {}) {
  return {
    action,
    status,
    details: {
      path: window.location.pathname,
      href: window.location.href,
      user_agent: navigator.userAgent,
      ...details,
    },
  };
}

function sendAudit(token, payload) {
  try {
    fetch(AUDIT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      // keepalive matters more now that the send can be deferred: it lets a
      // request already in flight complete even if the page is unloading.
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Telemetry must never affect the user workflow.
  }
}

/**
 * @param {object}  [options]
 * @param {boolean} [options.immediate] send now instead of at idle. Used for
 *   errors: if the page is failing, the record must not wait for an idle
 *   moment that a crashing page may never reach.
 */
export function trackFrontendEvent(action, details = {}, status = "ok", options = {}) {
  if (typeof window === "undefined") return;

  const token = getToken();
  if (!token) return;

  const payload = buildPayload(action, status, details);

  // Nothing on screen waits for an audit write, and it was measured taking
  // 1,176-1,629 ms inside the dashboard's first request wave. Defer it past
  // first paint — deferIdle still flushes on pagehide, so the write is delayed,
  // never dropped.
  if (options.immediate) sendAudit(token, payload);
  else deferIdle(() => sendAudit(token, payload));
}

const EXTENSION_URL = /^(?:chrome|moz|safari-web|safari|ms-browser)-extension:\/\//;
const STACK_URL = /\b[a-z][a-z0-9+.-]*:\/\/[^\s)]+/gi;

// A browser extension's script runs in our page and its failures reach our
// window handlers, but they are not ours to fix. One extension on one admin's
// browser (25-28 Sep 2026) rejected a promise twice a second for three days and
// wrote 571,276 identical rows — 476 MB, enough to take the database past its
// 500 MB cap on its own. Dropped only when EVERY frame we can see is an
// extension's: a stack that passes through our code is still our bug.
export function isExtensionOnlyError(stack, filename) {
  if (filename && EXTENSION_URL.test(filename)) return true;
  const urls = String(stack || "").match(STACK_URL) || [];
  return urls.length > 0 && urls.every((url) => EXTENSION_URL.test(url));
}

// Per page load. An error thrown in a loop is one fact, not thousands: the
// first few copies say everything the rest would. The server enforces its own
// ceiling too (routes/audit.js) — this one only saves the requests.
const MAX_REPORTS_PER_ERROR = 3;
const MAX_ERROR_REPORTS = 25;
const reportCounts = new Map();
let reportsSent = 0;

export function trackFrontendError(error, context = {}) {
  const reason = error?.reason || error;
  const message = compact(reason?.message || reason, "Frontend error");
  const stack = compact(reason?.stack, "");

  if (isExtensionOnlyError(stack, context.filename)) return;

  const seen = reportCounts.get(message) || 0;
  if (seen >= MAX_REPORTS_PER_ERROR || reportsSent >= MAX_ERROR_REPORTS) return;
  reportCounts.set(message, seen + 1);
  reportsSent++;

  trackFrontendEvent(
    "frontend.error",
    {
      message,
      stack,
      source: context.source || "unknown",
      component_stack: compact(context.componentStack, ""),
      filename: compact(context.filename, ""),
      lineno: context.lineno || null,
      colno: context.colno || null,
    },
    "fail",
    // Errors are NOT deferred. A page that is failing may never reach an idle
    // moment, and the crash report is exactly the record you cannot afford to
    // lose to save a few hundred milliseconds on a page that is already broken.
    { immediate: true }
  );
}

export function reportWebVital(metric) {
  trackFrontendEvent("frontend.web_vital", {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
  });
}
